import { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { getLang, t } from '../lib/i18n';
import { pointsAPI } from '../lib/points';
import MyLearningShell from '../components/MyLearningShell';
import { EmptyState, panelCls } from '../components/ui';

// My learning → Points (MMS_POINTS_HUB_PLAN F4). Only the LMS actions: what each
// one is worth, how often you've done it, and your latest LMS points. The whole
// picture across products lives in My Account → Points & Rewards.

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();
const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(getLang() === 'es' ? 'es' : 'en', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};

function limits(a) {
  const out = [];
  if (a.points_per) out.push(t('per purchase'));
  else if (a.once_per_subject) out.push(t('once each'));
  if (a.caps?.per_day) out.push(`${t('up to')} ${a.caps.per_day} ${t('per day')}`);
  if (a.caps?.total) out.push(`${t('up to')} ${a.caps.total} ${t('in total')}`);
  return out.join(' · ');
}

export default function MyPoints() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    pointsAPI.mine().then(({ data: d }) => setData(d)).catch(() => setFailed(true));
  }, []);

  if (failed) {
    return <MyLearningShell><EmptyState>{t('Points are not available right now. Try again in a moment.')}</EmptyState></MyLearningShell>;
  }
  if (!data) return <MyLearningShell><p className="text-sm text-lms-muted">{t('Loading…')}</p></MyLearningShell>;

  const me = data.me || { points: 0, xp: 0, balance: 0, counts: {}, recent: [] };
  const actions = data.actions || [];
  const names = Object.fromEntries(actions.map((a) => [a.trigger, a.name]));

  return (
    <MyLearningShell>
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className={`${panelCls} p-5`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-lms-faint">{t('Earned in courses')}</div>
            <div className="mt-1.5 flex items-center gap-2 text-[28px] font-bold text-lms-ink lms-num"><Trophy size={22} className="text-lms-accent" />{fmt(me.points)}</div>
            <div className="text-xs text-lms-muted lms-num">{fmt(me.xp)} XP</div>
          </div>
          <div className={`${panelCls} p-5`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-lms-faint">{t('Total balance')}</div>
            <div className="mt-1.5 text-[28px] font-bold text-lms-ink lms-num">{fmt(me.balance)}</div>
            <div className="text-xs text-lms-muted">{t('Across every product, in My Account')}</div>
          </div>
          <div className={`${panelCls} p-5`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-lms-faint">{t('Lessons completed')}</div>
            <div className="mt-1.5 text-[28px] font-bold text-lms-ink lms-num">{fmt(me.counts?.lesson_completed)}</div>
            <div className="text-xs text-lms-muted lms-num">{fmt(me.counts?.course_completed)} {t('courses completed')}</div>
          </div>
        </div>

        <section>
          <h2 className="mb-1 text-lg font-bold text-lms-ink">{t('How to earn points here')}</h2>
          <p className="mb-3 text-sm text-lms-muted">{t('Every action below adds points to your balance. Points are awarded a few seconds after the action.')}</p>
          {actions.length === 0 ? (
            <EmptyState>{t('No point actions are active in the LMS right now.')}</EmptyState>
          ) : (
            <div className={`${panelCls} overflow-x-auto`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-lms-border text-left text-xs text-lms-faint">
                    <th className="px-4 py-2.5 font-medium">{t('Action')}</th>
                    <th className="px-3 py-2.5 font-medium">{t('Limits')}</th>
                    <th className="px-3 py-2.5 text-right font-medium">{t('Times')}</th>
                    <th className="px-4 py-2.5 text-right font-medium">{t('Points')}</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a) => (
                    <tr key={a.id} className="border-b border-lms-border last:border-b-0">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-lms-ink">{t(a.name)}</div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-lms-muted">{limits(a) || '—'}</td>
                      <td className="px-3 py-2.5 text-right lms-num text-lms-muted">{fmt(me.counts?.[a.trigger])}</td>
                      <td className="px-4 py-2.5 text-right font-semibold lms-num text-lms-ink">
                        +{fmt(a.points)}{a.points_per ? <span className="text-xs font-normal text-lms-faint"> / US$ 1</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-lg font-bold text-lms-ink">{t('Latest points')}</h2>
          {(me.recent || []).length === 0 ? (
            <EmptyState>{t('Complete your first lesson to earn your first points.')}</EmptyState>
          ) : (
            <div className={`${panelCls} overflow-x-auto`}>
              <table className="w-full text-sm">
                <tbody>
                  {me.recent.map((r) => (
                    <tr key={r.id} className="border-b border-lms-border last:border-b-0">
                      <td className="px-4 py-2.5 text-lms-ink">{t(r.rule_name || names[r.event_type] || r.event_type)}</td>
                      <td className="px-3 py-2.5 text-xs text-lms-faint lms-num">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold lms-num text-lms-ink">+{fmt(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </MyLearningShell>
  );
}
